// Sprachwahl der Self-Service-Fläche (Spec 2026-09-15 Sprachumschalter): kleiner
// React-Context statt i18n-Bibliothek oder zustand (wie im Workshops-Tool) —
// ~80 Texte, zwei Sprachen, eine Fläche.
//
// Startwert: gespeicherte Wahl (`localStorage['tro-self-lang']`), sonst
// Browsersprache (`de-*` → Deutsch, alles andere Englisch). Jeder Speicherzugriff
// in try/catch: privates Fenster oder gesperrter Speicher dürfen die Seite nicht
// brechen, dann gilt eben die Browsersprache und die Wahl hält bis zum Neuladen.
//
// Tests übergeben `initialLang` ausdrücklich — sonst entschiede jsdoms
// `navigator.language` (`en-US`) über die Sprache jeder Zusicherung.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { de, type Messages } from './de';
import { en } from './en';
import type { Lang } from './lang';

export type { Lang } from './lang';
export type { Messages } from './de';

export const LANG_STORAGE_KEY = 'tro-self-lang';

export const MESSAGES: Record<Lang, Messages> = { de, en };

/** Startsprache: gespeicherte Wahl, sonst Browsersprache. */
export function readInitialLang(): Lang {
  try {
    const gespeichert = localStorage.getItem(LANG_STORAGE_KEY);
    if (gespeichert === 'de' || gespeichert === 'en') return gespeichert;
  } catch {
    // Speicher gesperrt → Browsersprache
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';
  return (browser ?? '').toLowerCase().startsWith('de') ? 'de' : 'en';
}

interface LanguageApi {
  lang: Lang;
  setLang: (next: Lang) => void;
  t: Messages;
}

const LanguageContext = createContext<LanguageApi | null>(null);

export function LanguageProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initialLang ?? readInitialLang());

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // Speicher gesperrt → Wahl gilt nur bis zum Neuladen
    }
  }, []);

  // `<html lang>` folgt der Wahl (Screenreader, Silbentrennung, Browser-Übersetzer).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const api = useMemo<LanguageApi>(() => ({ lang, setLang, t: MESSAGES[lang] }), [lang, setLang]);
  return <LanguageContext.Provider value={api}>{children}</LanguageContext.Provider>;
}

/** Sprache, Umschalter und Wörterbuch der Self-Service-Fläche. */
export function useT(): LanguageApi {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useT außerhalb des LanguageProvider');
  return ctx;
}
